import {
	AUTO_SCROLL_EDGE_PX,
	AUTO_SCROLL_MAX_SPEED_PX,
	AUTO_SCROLL_MIN_SPEED_PX,
} from './constants';

type Bounds = { top: number; bottom: number };

/** Nearest ancestor that actually scrolls; `null` means the page itself does. */
const scrollingAncestor = (from: HTMLElement): HTMLElement | null => {
	for (let node = from.parentElement; node; node = node.parentElement) {
		const { overflowY } = getComputedStyle(node);
		const scrolls = overflowY === 'auto' || overflowY === 'scroll';

		if (scrolls && node.scrollHeight > node.clientHeight) return node;
	}

	return null;
};

const boundsOf = (scroller: HTMLElement | null): Bounds => {
	if (!scroller) return { top: 0, bottom: window.innerHeight };

	const { top, bottom } = scroller.getBoundingClientRect();
	return { top, bottom };
};

/** Ramps from a crawl at the edge zone's inner limit to full speed at the edge itself. */
const speedFor = (distance: number): number => {
	const ratio = 1 - Math.max(distance, 0) / AUTO_SCROLL_EDGE_PX;
	return AUTO_SCROLL_MIN_SPEED_PX + ratio * (AUTO_SCROLL_MAX_SPEED_PX - AUTO_SCROLL_MIN_SPEED_PX);
};

/** Scrolls near the top and bottom edges; browsers only do this for their own text drags. */
export class DragAutoScroll {
	private scroller: HTMLElement | null = null;
	private resolved = false;
	private velocity = 0;
	private frame = 0;

	constructor(
		private editorDom: HTMLElement,
		/** Runs after every scroll step, so the caller can re-place its drop indicator. */
		private onScroll: () => void
	) {}

	stop() {
		cancelAnimationFrame(this.frame);
		this.frame = 0;
		this.velocity = 0;
		this.resolved = false;
		this.scroller = null;
	}

	/** Called on every `dragover`; a pointer away from the edges simply parks the loop. */
	update(pointerY: number) {
		if (!this.resolved) {
			this.scroller = scrollingAncestor(this.editorDom);
			this.resolved = true;
		}

		const { top, bottom } = boundsOf(this.scroller);
		const fromTop = pointerY - top;
		const fromBottom = bottom - pointerY;

		if (fromTop < AUTO_SCROLL_EDGE_PX) this.velocity = -speedFor(fromTop);
		else if (fromBottom < AUTO_SCROLL_EDGE_PX) this.velocity = speedFor(fromBottom);
		else this.velocity = 0;

		if (this.velocity) this.start();
	}

	private start() {
		if (this.frame) return;

		const step = () => {
			if (!this.velocity) {
				this.frame = 0;
				return;
			}

			const before = this.scroller ? this.scroller.scrollTop : window.scrollY;
			if (this.scroller) this.scroller.scrollTop = before + this.velocity;
			else window.scrollBy(0, this.velocity);

			const after = this.scroller ? this.scroller.scrollTop : window.scrollY;
			// Nothing moved: we are at an end stop, so stop burning frames.
			if (after === before) {
				this.frame = 0;
				this.velocity = 0;
				return;
			}

			this.onScroll();
			this.frame = requestAnimationFrame(step);
		};

		this.frame = requestAnimationFrame(step);
	}
}
